import { makeAutoObservable } from 'mobx';

export interface Metadata {
  [key: string]: string | number | boolean;
}

export interface SearchResultMetadata {
  vector_query_index: number | null;
  bm25_query_index: number | null;
  search_type: 'vector' | 'bm25';
  score: number;
}

export interface InputsDbItem {
  id: string;
  type: string;
  app: string;
  created_at: string;
  text: string;
  text_size: number;
  kind: string;
  embedded: boolean;
  metadata?: Metadata;
}

export interface InputsDbResponse {
  results: InputsDbItem[];
  total: number;
  apps: string[];
  search_metadata?: Record<string, SearchResultMetadata[]>;
}

export type InputsDbSort = 'relevance' | 'recent';

/** Window of captures to show, as ISO instants. An empty `app` means every app. */
export interface InputsDbTimeRange {
  app: string;
  after: string;
  before: string;
}

export interface InputsDbFilters {
  type: string[];
  app: string;
  search: string;
  ckJobId: string;
  embedded?: boolean;
  after?: string;
  before?: string;
  isHybridSearch?: boolean;
  sortBy: InputsDbSort;
}

export const getItemMetadata = (
  itemId: string,
  searchMetadata?: Record<string, SearchResultMetadata[]>
): SearchResultMetadata[] => {
  return searchMetadata?.[itemId] || [];
};

export const getBM25Score = (
  metadata: SearchResultMetadata[]
): number | null => {
  const bm25Result = metadata.find((m) => m.search_type === 'bm25');
  return bm25Result ? bm25Result.score : null;
};

export const getVectorScore = (
  metadata: SearchResultMetadata[]
): number | null => {
  const vectorResult = metadata.find((m) => m.search_type === 'vector');
  return vectorResult ? vectorResult.score : null;
};

export const getCkJobId = (metadata?: Metadata): string => {
  const value = metadata?.ck_job_id;
  return typeof value === 'string' ? value : '';
};

class InputsDbStore {
  filters: InputsDbFilters = {
    type: [],
    app: '',
    search: '',
    ckJobId: '',
    embedded: undefined,
    after: undefined,
    before: undefined,
    isHybridSearch: false,
    sortBy: 'relevance',
  };

  constructor() {
    makeAutoObservable(this);
  }

  updateFilters(partial: Partial<InputsDbFilters>) {
    Object.assign(this.filters, partial);
  }

  resetFilters() {
    this.filters = {
      type: ['input'],
      app: '',
      search: '',
      ckJobId: '',
      embedded: undefined,
      after: undefined,
      before: undefined,
      isHybridSearch: false,
      sortBy: 'relevance',
    };
  }

  showInputsForCkJobId(ckJobId: string) {
    this.filters = {
      type: ['input'],
      app: '',
      search: '',
      ckJobId,
      embedded: undefined,
      after: undefined,
      before: undefined,
      isHybridSearch: false,
      sortBy: 'relevance',
    };
  }

  showInputsForTimeRange({ app, after, before }: InputsDbTimeRange) {
    this.filters = {
      type: ['input'],
      app,
      search: '',
      ckJobId: '',
      embedded: undefined,
      after,
      before,
      isHybridSearch: false,
      sortBy: 'recent',
    };
  }
}

export const inputsDbStore = new InputsDbStore();

export const inputsDbAtoms = {
  get filters() {
    return inputsDbStore.filters;
  },
  set filters(filters: InputsDbFilters) {
    inputsDbStore.filters = filters;
  },
  useFilters: () => inputsDbStore.filters,
  updateFilters: (partial: Partial<InputsDbFilters>) =>
    inputsDbStore.updateFilters(partial),
  resetFilters: () => inputsDbStore.resetFilters(),
  showInputsForCkJobId: (ckJobId: string) =>
    inputsDbStore.showInputsForCkJobId(ckJobId),
  showInputsForTimeRange: (range: InputsDbTimeRange) =>
    inputsDbStore.showInputsForTimeRange(range),
};
